import React from "react";
import { Box, IconButton, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Account } from "@rentablo/api/apiv1";
import { LoadingButton } from "@rentablo/components";
import UnlinkIcon from "@icons/myicons/unlink.svg?react";
import { ContraAccountRow } from "./ContraAccountRow";

const GroupLabel = styled(Typography)(({ theme }) => ({
  display: "block",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
}));

const RowList = styled("div")(({ theme }) => ({
  "& > *": {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3.5),
  },
  "& > * + *": {
    borderTop: `1px solid ${theme.palette.border.secondary}`,
    paddingTop: theme.spacing(3.5),
  },
}));

export const PrimaryContraRow = ({ account }: { account: Account }) => {
  const { t } = useTranslation();
  return (
    <>
      <GroupLabel variant="overline" color="textSecondary">
        {t("createAndEditAccount.additionalContraAccounts.primaryLabel")}
      </GroupLabel>
      <RowList>
        <ContraAccountRow
          accountName={account.name}
          iban={account.iban}
          bankImage={account.iconUrl}
          type={account.typeFromBank}
          to={`/accounts/${account.id}`}
        />
      </RowList>
    </>
  );
};

interface AdditionalContraListProps {
  accounts: Account[];
  isMutating: boolean;
  pendingAccountId?: number;
  onSetPrimary: (id: number) => void;
  onRemove: (id: number) => void;
}

export const AdditionalContraList = ({
  accounts,
  isMutating,
  pendingAccountId,
  onSetPrimary,
  onRemove,
}: AdditionalContraListProps) => {
  const { t } = useTranslation();
  return (
    <>
      <GroupLabel variant="overline" color="textSecondary">
        {t("createAndEditAccount.additionalContraAccounts.additionalLabel")}
      </GroupLabel>
      <RowList>
        {accounts.map((account) => (
          <ContraAccountRow
            key={account.id}
            accountName={account.name}
            iban={account.iban}
            bankImage={account.iconUrl}
            type={account.typeFromBank}
            to={`/accounts/${account.id}`}
            action={
              <Box display="flex" alignItems="center" gap={1}>
                <LoadingButton
                  variant="text"
                  size="small"
                  data-testid={`set-primary-${account.id}`}
                  onClick={() => onSetPrimary(account.id)}
                  disabled={isMutating}
                  isFetching={pendingAccountId === account.id}
                >
                  {t(
                    "createAndEditAccount.additionalContraAccounts.setAsPrimary"
                  )}
                </LoadingButton>
                <IconButton
                  size="small"
                  data-testid={`remove-contra-${account.id}`}
                  onClick={() => onRemove(account.id)}
                  disabled={isMutating}
                  aria-label={t(
                    "createAndEditAccount.additionalContraAccounts.removeButton"
                  )}
                >
                  <UnlinkIcon width={18} height={18} />
                </IconButton>
              </Box>
            }
          />
        ))}
      </RowList>
    </>
  );
};
